/**
 * useSeatOperation.ts
 * 좌석 운영 모드 Hook
 *
 * Features:
 * - 좌석 현황 수동 새로고침 (폴링 제거)
 * - 좌석 상세 정보 조회
 * - 순찰 노트 작성
 * - 학생 배정 해제
 */

import { useState, useEffect, useCallback } from 'react';
import {
  seatOperationRepository,
  RuntimeFloorLayout,
  SeatDetailInfo,
  PatrolNoteCreate,
  PatrolNote,
} from '../repositories/SeatOperationRepository';

export interface UseSeatOperationProps {
  floorId: string | null;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export interface UseSeatOperationReturn {
  layout: RuntimeFloorLayout | null;
  seatDetail: SeatDetailInfo | null;
  loading: boolean;
  error: string | null;
  refreshLayout: () => Promise<void>;
  loadSeatDetail: (seatId: string) => Promise<void>;
  createNote: (seatId: string, note: PatrolNoteCreate) => Promise<PatrolNote>;
  unassignStudent: (seatId: string) => Promise<void>;
  clearSeatDetail: () => void;
}

export const useSeatOperation = ({
  floorId,
  autoRefresh = false,  // 폴링 비활성화 (수동 새로고침만 사용)
  refreshInterval = 30000,
}: UseSeatOperationProps): UseSeatOperationReturn => {
  const [layout, setLayout] = useState<RuntimeFloorLayout | null>(null);
  const [seatDetail, setSeatDetail] = useState<SeatDetailInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshLayout = useCallback(async () => {
    if (!floorId) {
      setLayout(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await seatOperationRepository.getRuntimeLayout(floorId);
      setLayout(data);
    } catch (err: any) {
      setError(err.message || '좌석 현황을 불러오는데 실패했습니다.');
      console.error('[useSeatOperation] refreshLayout 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [floorId]);

  const loadSeatDetail = useCallback(async (seatId: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await seatOperationRepository.getSeatDetail(seatId);
      setSeatDetail(data);
    } catch (err: any) {
      setError(err.message || '좌석 정보를 불러오는데 실패했습니다.');
      console.error('[useSeatOperation] loadSeatDetail 실패:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createNote = useCallback(
    async (seatId: string, note: PatrolNoteCreate): Promise<PatrolNote> => {
      setLoading(true);
      setError(null);

      try {
        const result = await seatOperationRepository.createPatrolNote(seatId, note);

        if (seatDetail && seatDetail.seat.seat_id === seatId) {
          await loadSeatDetail(seatId);
        }

        return result;
      } catch (err: any) {
        setError(err.message || '순찰 노트 작성에 실패했습니다.');
        console.error('[useSeatOperation] createNote 실패:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [seatDetail, loadSeatDetail]
  );

  const unassignStudent = useCallback(
    async (seatId: string) => {
      setLoading(true);
      setError(null);

      try {
        await seatOperationRepository.unassignStudent(seatId);
        await refreshLayout();
        setSeatDetail(null);
      } catch (err: any) {
        setError(err.message || '학생 배정 해제에 실패했습니다.');
        console.error('[useSeatOperation] unassignStudent 실패:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [refreshLayout]
  );

  const clearSeatDetail = useCallback(() => {
    setSeatDetail(null);
  }, []);

  useEffect(() => {
    if (floorId) {
      refreshLayout();
    }
  }, [floorId, refreshLayout]);

  useEffect(() => {
    if (!autoRefresh || !floorId) {
      return;
    }

    const intervalId = setInterval(() => {
      refreshLayout();
    }, refreshInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [autoRefresh, floorId, refreshInterval, refreshLayout]);

  return {
    layout,
    seatDetail,
    loading,
    error,
    refreshLayout,
    loadSeatDetail,
    createNote,
    unassignStudent,
    clearSeatDetail,
  };
};
